import {
  ConsumableContract,
  ElectricityContract,
  HeatContract,
  IUtilityContract,
  IUtilityContractGroup,
  ResidualWasteContract,
  UtilityType,
  WaterContract,
} from "@/api/facilities";
import HeatIcon from "@/components/icons/HeatIcon";
import WaterIcon from "@/components/icons/WaterIcon";
import StatusTag from "@/components/StatusTag/StatusTag";
import { Button, Switch } from "antd";
import { ReactNode, useState } from "react";
import styles from "../FacilityDetail.module.scss";

import { getFacilityGoodsAndServicesTemplate, getLogisticsPackaging } from "@/api/template";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import FileUploader from "@/components/FileUploader/FileUploader";
import CapitalGoodsIcon from "@/components/icons/CapitalGoodsIcon";
import PackageIcon from "@/components/icons/chapters/PackageIcon";
import ConsumableIcon from "@/components/icons/ConsumableIcon";
import CoolingIcon from "@/components/icons/CoolingIcon";
import ElectricityIcon from "@/components/icons/ElectricityIcon";
import { ResidualWasteIcon } from "@/components/icons/ResidualWasteIcon";
import Skeleton from "@/components/Skeleton/Skeleton";
import { useUpdateFacilityUsed } from "@/hooks/useFacilities";
import ResidualWasteModal from "@/pages/facilities/detail/Utilities/modals/ResidualWasteModal/ResidualWasteModal";
import { DownloadOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import CoolingModal from "./modals/CoolingModal/CoolingModal";
import ElectricityContractModal from "./modals/ElectricityContractModal/ElectricityContractModal";
import HeatContractModal from "./modals/HeatContractModal/HeatContractModal";
import UtilityConsumableModal from "./modals/UtilityConsumableModal/UtilityConsumableModal";
import WaterContractModal from "./modals/WaterModal/WaterContractModal";
import UtilityContract from "./UtilityContract";

export const UtilityContractGroupInfo: Record<UtilityType, { title: string; icon: ReactNode }> = {
  electricity: {
    title: "Electricity",
    icon: (
      <ElectricityIcon
        width={24}
        height={24}
      />
    ),
  },
  heat: {
    title: "Heat",
    icon: (
      <HeatIcon
        width={24}
        height={24}
      />
    ),
  },
  water: {
    title: "Water",
    icon: (
      <WaterIcon
        width={24}
        height={24}
      />
    ),
  },
  cooling: {
    title: "Cooling",
    icon: (
      <CoolingIcon
        width={24}
        height={24}
      />
    ),
  },
  consumable: {
    title: "Consumables",
    icon: (
      <ConsumableIcon
        width={24}
        height={24}
      />
    ),
  },
  residual_waste: {
    title: "Residual waste",
    icon: (
      <ResidualWasteIcon
        width={20}
        height={20}
      />
    ),
  },
  goods_and_services: {
    title: "Goods & services",
    icon: (
      <CapitalGoodsIcon
        width={24}
        height={24}
      />
    ),
  },
  packaging: {
    title: "Logistics packaging",
    icon: (
      <PackageIcon
        width={20}
        height={20}
      />
    ),
  },
};

export default function UtilityContractGroup({
  facilityId,
  type,
  contractGroup,
  year,
  isLoading,
  company_name,
}: {
  facilityId: number;
  type: UtilityType;
  contractGroup?: IUtilityContractGroup;
  year: number;
  isLoading: boolean;
  company_name?: string;
}) {
  const info = UtilityContractGroupInfo[type];

  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractToEdit, setContractToEdit] = useState<IUtilityContract>();
  const updateUsed = useUpdateFacilityUsed();

  const toggleUsed = () => {
    if (!contractGroup || !year) return;
    updateUsed.mutateAsync({
      facilityId: facilityId,
      setUsed: {
        year,
        utility_type: type,
        used: !contractGroup?.used,
      },
    });
  };

  const editContract = (contract: IUtilityContract) => {
    setContractToEdit(contract);
    setContractModalOpen(true);
  };

  const closeContractModal = () => {
    setContractModalOpen(false);
    setContractToEdit(undefined);
  };

  // for water, electricity and heat we can have multiple contracts and thus multiple labels
  // for the others, it's just 1 label which is not used (we call it "contract" here)
  const sourceLabels = ["water", "electricity", "heat", "cooling"].includes(type)
    ? contractGroup?.contracts
        .map((contract) => contract.sources.map((source) => source.label))
        .flat()
        .filter((value, index, array) => array.indexOf(value) === index) || []
    : ["contract"];

  const columns =
    type === "consumable"
      ? ["name", "amount"]
      : type === "goods_and_services"
        ? ["name", "amount"]
        : type === "packaging"
          ? ["name", "amount", "supplier name", "supplier address"]
          : type !== "residual_waste"
            ? ["range", ...sourceLabels, ""]
            : ["name", "amount", "impact reference"];

  const minDate = dayjs(String(year) + "-01-01");
  const maxDate = dayjs(String(year + 1) + "-01-01").subtract(1, "day");

  const modal: Record<UtilityType, ReactNode> = {
    electricity: (
      <ElectricityContractModal
        facilityId={facilityId}
        key={`${contractToEdit?.id}${contractModalOpen}`}
        editContract={contractToEdit as ElectricityContract}
        open={contractModalOpen}
        closeModal={closeContractModal}
        minDate={minDate}
        maxDate={maxDate}
      />
    ),
    heat: (
      <HeatContractModal
        facilityId={facilityId}
        key={`${contractToEdit?.id}${contractModalOpen}`}
        editContract={contractToEdit as HeatContract}
        open={contractModalOpen}
        closeModal={closeContractModal}
        minDate={minDate}
        maxDate={maxDate}
      />
    ),
    water: (
      <WaterContractModal
        facilityId={facilityId}
        key={`${contractToEdit?.id}${contractModalOpen}`}
        editContract={contractToEdit as WaterContract}
        open={contractModalOpen}
        closeModal={closeContractModal}
        minDate={minDate}
        maxDate={maxDate}
      />
    ),
    residual_waste: (
      <ResidualWasteModal
        facilityId={facilityId}
        key={`${contractToEdit?.id}${contractModalOpen}`}
        editContract={contractToEdit as ResidualWasteContract}
        open={contractModalOpen}
        closeModal={closeContractModal}
        minDate={minDate}
        maxDate={maxDate}
      />
    ),
    cooling: (
      <CoolingModal
        facilityId={facilityId}
        key={`${contractToEdit?.id}${contractModalOpen}`}
        editContract={contractToEdit as WaterContract}
        open={contractModalOpen}
        closeModal={closeContractModal}
        minDate={minDate}
        maxDate={maxDate}
      />
    ),
    goods_and_services: null,
    consumable: (
      <UtilityConsumableModal
        facilityId={facilityId}
        key={`${contractToEdit?.id}${contractModalOpen}`}
        editContract={contractToEdit as ConsumableContract}
        open={contractModalOpen}
        closeModal={closeContractModal}
        minDate={minDate}
        maxDate={maxDate}
      />
    ),
    packaging: null,
  };

  const bulkUploaders: Record<UtilityType, ReactNode> = {
    electricity: null,
    heat: null,
    water: null,
    residual_waste: null,
    cooling: null,
    consumable: null,
    packaging: (
      <FileUploader
        uploadType={"facility_logistics_packaging"}
        buttonText={""}
        disabled={isLoading || !contractGroup}
        uploadDataTableProps={{
          columns: [
            { key: "material_name", label: "Material" },
            { key: "mass", label: "Mass (kg)" },
            { key: "supplier_name", label: "Supplier name" },
            { key: "supplier_address", label: "Supplier address" },
          ],
          rowKey: "material_name",
        }}
        uploadParameters={{
          facilityId: facilityId,
          year: year,
        }}
        customRender={(props) => (
          <Button
            {...props}
            size={"small"}
          >
            <UploadOutlined />
          </Button>
        )}
        note={
          <div className={styles.uploadNote}>
            <h1 className={styles.title}>How it works</h1>
            <p className={styles.description}>
              Upload your logistics packaging data using the provided{" "}
              <FileDownloader
                tooltipText="Download template"
                fileOutputName={`template-facility-logistics-packaging.xlsx`}
                fileUrlFn={getLogisticsPackaging}
                buttonText={"template."}
                icon={<DownloadOutlined />}
                type="link"
              />
            </p>
          </div>
        }
      />
    ),
    goods_and_services: (
      <FileUploader
        uploadType={"facility_goods_and_services"}
        buttonText={""}
        disabled={isLoading || !contractGroup}
        templateUrlFn={getFacilityGoodsAndServicesTemplate}
        uploadDataTableProps={{
          columns: [
            { key: "name", label: "Name" },
            { key: "quantity", label: "Quantity" },
            { key: "unit", label: "Unit" },
          ],
          rowKey: "name",
        }}
        uploadParameters={{
          facilityId: facilityId,
          year: year,
        }}
        customRender={(props) => (
          <Button
            {...props}
            size={"small"}
          >
            <UploadOutlined />
          </Button>
        )}
        note={
          <div className={styles.uploadNote}>
            <h1 className={styles.title}>How it works</h1>
            <p className={styles.description}>
              Add data on the goods and services your company purchases, such as office supplies, IT
              equipment, logistics, or business services.{" "}
              <FileDownloader
                tooltipText="Download template"
                fileOutputName={`template-facility-goods-and-services.xlsx`}
                fileUrlFn={getFacilityGoodsAndServicesTemplate}
                buttonText={"template"}
                icon={<DownloadOutlined />}
                type="link"
              />
            </p>
          </div>
        }
      />
    ),
  };

  const modalUpload = modal[type] !== null;
  const bulkUploader = bulkUploaders[type];

  return (
    <div
      className={[
        styles["contract-group"],
        contractGroup?.used ? styles.disabled : "",
        contractGroup?.status === "incomplete" ? styles.incomplete : "",
      ].join(" ")}
    >
      <div className={styles["contract-group-header"]}>
        <div className={styles.title}>
          <div className={styles.iconContainer}>{info.icon}</div>
          <span className={styles.type}>{info.title}</span>
        </div>
        <div className={styles.actions}>
          {isLoading || !contractGroup ? (
            <>
              <Skeleton style={{ width: "60px", height: "20px" }} />
              <Switch
                size="small"
                disabled={true}
              />
              {modalUpload && (
                <Button
                  size="small"
                  disabled={true}
                >
                  <PlusOutlined />
                </Button>
              )}
              {bulkUploader !== null && bulkUploader}
            </>
          ) : (
            <>
              <StatusTag
                status={contractGroup.status || "incomplete"}
                showIcon={false}
                background="white"
              />
              {(!contractGroup.used || contractGroup.contracts.length === 0) && (
                <Switch
                  size="small"
                  checked={contractGroup.used}
                  onChange={toggleUsed}
                />
              )}
              {modalUpload && (
                <Button
                  size="small"
                  onClick={() => setContractModalOpen(true)}
                >
                  <PlusOutlined />
                </Button>
              )}
              {bulkUploader !== null && bulkUploader}
            </>
          )}
        </div>
      </div>
      {contractGroup?.used && contractGroup && contractGroup.contracts.length > 0 && (
        <>
          <div className={styles.spacer}></div>
          <table>
            <thead>
              <tr>
                {columns.map((source) => (
                  <th key={source}>{source}</th>
                ))}
                <th className={styles.lastEdit}>Last edit</th>
                <th className={styles.actions}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contractGroup?.contracts.map((contract) => (
                <UtilityContract
                  facilityId={facilityId}
                  key={contract.id}
                  contract={contract}
                  sourceLabels={sourceLabels}
                  editContract={editContract}
                  showRange={columns.includes("range")}
                  companyName={company_name}
                  modalUpload={modalUpload}
                />
              ))}
            </tbody>
          </table>
        </>
      )}
      {modal[type]}
    </div>
  );
}
